import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isSuperAdmin } from "@/types";
import type { AuthUser, Candidate } from "@/types";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { extractTextFromFile } from "@/lib/extractText";
import { scoreColor, barColor, recStyles, recLabel } from "@/lib/scoreUtils";
import { timeAgo, fullDate } from "@/lib/dateUtils";
import { useReEvaluation } from "@/hooks/useReEvaluation";
import { ScoreRing } from "../common/ScoreRing";
import { StarRating } from "../common/StarRating";
import { OrgAssignRow } from "../row/OrgAssignRow";
import { StageBadge, StageActions } from "../common/StageActions";

interface CandidateCardProps {
  candidate: Candidate;
  positionTitle?: string;
  currentUser?: AuthUser;
  onArchive?: (id: number, archived: boolean) => void;
  onDelete?: (id: number) => void;
  onViewProfile?: (id: number) => void;
  initialExpanded?: boolean;
  onExpand?: (id: number | null) => void;
}

export function CandidateCard({
  candidate,
  positionTitle,
  currentUser,
  onArchive,
  onDelete,
  onViewProfile,
  initialExpanded,
  onExpand,
}: CandidateCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isArchived = candidate.archived;
  const isRejected =
    candidate.recommendation === "reject" ||
    candidate.current_stage === "rejected" ||
    candidate.auto_rejected === true;
  const [expanded, setExpanded] = useState(initialExpanded ?? false);
  const [reUploading, setReUploading] = useState(false);
  const [showAddInfo, setShowAddInfo] = useState(false);
  const [addInfoText, setAddInfoText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { reEvaluating, startReEvaluation } = useReEvaluation();

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReUploading(true);
    try {
      const result = await extractTextFromFile(file);
      await api.updateCandidateResume(candidate.id, {
        resume_text: result.text,
        filename: file.name,
        resume_file_base64: result.fileBase64,
        resume_file_mime: result.fileMime,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.candidateProfile(candidate.id) });
      startReEvaluation();
    } catch (err) {
      alert(`Failed to update resume: ${(err as Error).message}`);
    } finally {
      setReUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAddInfo = async () => {
    if (!addInfoText.trim()) return;
    try {
      await api.addCandidateInfo(candidate.id, addInfoText.trim());
      setAddInfoText("");
      setShowAddInfo(false);
      startReEvaluation();
    } catch (err) {
      alert(`Failed to add info: ${(err as Error).message}`);
    }
  };

  const failedFilters = (candidate.filter_results ?? []).filter((f) => f.status === "fail");
  const indeterminateFilters = (candidate.filter_results ?? []).filter((f) => f.status === "indeterminate");

  return (
    <div className="rounded-card bg-white shadow-card transition-shadow hover:shadow-card-hover">
      {reEvaluating && (
        <div className="flex items-center gap-2 rounded-t-card border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs font-medium text-amber-800">
          <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
            <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Re-evaluating with updated information…
        </div>
      )}
      {candidate.auto_rejected && (
        <div className="flex items-start gap-2 rounded-t-card border-b border-rose-200 bg-rose-50 px-5 py-2.5 text-xs font-medium text-error-text">
          <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <div>
            <strong>Auto-rejected by filter:</strong>{" "}
            {failedFilters.map((f) => f.reason).join("; ")}
          </div>
        </div>
      )}
      {/* Header — always visible, clickable to expand/collapse */}
      <div
        className={`cursor-pointer p-5 ${isArchived ? "opacity-50" : ""}`}
        onClick={() => { const next = !expanded; setExpanded(next); onExpand?.(next ? candidate.id : null); }}
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <ScoreRing score={candidate.match_score} recommendation={candidate.recommendation} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-[22px] font-semibold leading-tight tracking-[-0.44px] text-near-black">
                    {candidate.name || "Unknown candidate"}
                  </h3>
                  <span
                    id="recommendation-badge"
                    className={`inline-flex rounded-badge px-3 py-1 text-[11px] font-semibold leading-tight ${recStyles[candidate.recommendation]}`}
                  >
                    {recLabel[candidate.recommendation]}
                  </span>
                  <StageBadge stage={candidate.current_stage} />
                </div>
                {(candidate.position_title || positionTitle || candidate.matrix_name) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {(candidate.position_title || positionTitle) && (
                      <span className="inline-flex items-center gap-1 rounded-badge border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                        <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                        {candidate.position_title || positionTitle}
                      </span>
                    )}
                    {candidate.matrix_name && (
                      <span className="inline-flex items-center gap-1 rounded-badge border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125h-1.5m1.5-1.125c0 .621-.504 1.125-1.125 1.125M18 18.375v-1.5c0-.621-.504-1.125-1.125-1.125m-9.75 0V6.375m0 0A1.125 1.125 0 0 1 8.25 5.25h7.5a1.125 1.125 0 0 1 1.125 1.125m-9.75 0A1.125 1.125 0 0 0 6 6.375m0 0V9m0 3.375v.75m0-4.125H6M12 9.75h.008v.008H12V9.75Zm0 3.375h.008v.008H12v-.008Z" />
                        </svg>
                        {candidate.matrix_name}
                      </span>
                    )}
                  </div>
                )}
                {isSuperAdmin(currentUser) && candidate.org_name && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-text-secondary">
                    <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                    </svg>
                    {candidate.org_name}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {candidate.resume_pdf_key && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/candidates/${candidate.id}/cv`); }}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
                    title="View CV / Resume"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                    View CV
                  </button>
                )}
                {!isRejected && !isArchived && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/candidates/${candidate.id}/interview`); }}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50"
                    title="Schedule an interview with this candidate"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    Schedule interview
                  </button>
                )}
                {!isRejected && !isArchived && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/candidates/${candidate.id}/endorse`); }}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
                    title="Generate Candidate Endorsement Form"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                    Endorse
                  </button>
                )}
                {onArchive && (
                  <button
                    onClick={() => onArchive(candidate.id, !isArchived)}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
                    title={isArchived ? "Unarchive" : "Archive"}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      {isArchived ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15M9 12l3 3m0 0 3-3m-3 3V2.25" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                      )}
                    </svg>
                    {isArchived ? "Unarchive" : "Archive"}
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(candidate.id)}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
                    title="Delete permanently"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <StarRating score={candidate.match_score} />
              <p className="text-[11px] font-semibold text-border-card" title={fullDate(candidate.created_at)}>
                Screened · {timeAgo(candidate.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable details */}
      {expanded && (
        <div className={`border-t border-surface-secondary px-8 pb-5 ${isArchived ? "opacity-50" : ""}`}>
          <div className="mt-4 flex items-start justify-between gap-6">
            <div id="candidate-details" className="flex flex-col gap-0.5 text-sm text-text-secondary">
              {candidate.email && (
                <span><span className="font-medium text-text-focused">Email:</span> {candidate.email}</span>
              )}
              {candidate.phone && (
                <span><span className="font-medium text-text-focused">Phone:</span> {candidate.phone}</span>
              )}
              {candidate.years_experience != null && (
                <span><span className="font-medium text-text-focused">Experience:</span> {candidate.years_experience} years</span>
              )}
              {candidate.education && (
                <span><span className="font-medium text-text-focused">Education:</span> {candidate.education}</span>
              )}
            </div>
            {candidate.photo && (
              <img
                src={candidate.photo}
                alt={candidate.name || "Candidate"}
                className="h-24 w-24 shrink-0 rounded-card object-cover shadow-card"
              />
            )}
          </div>

          {candidate.summary && (
            <p className="mt-4 text-sm leading-[1.43] text-text-focused">{candidate.summary}</p>
          )}

          {candidate.skills?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {candidate.skills.map((s) => (
                <span key={s} className="rounded-btn bg-surface-secondary px-2.5 py-0.5 text-xs font-medium text-near-black">
                  {s}
                </span>
              ))}
            </div>
          )}

          {candidate.matrix_breakdown && candidate.matrix_breakdown.length > 0 && (
            <div className="mt-4 space-y-3 border-t border-surface-secondary pt-4">
              <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
                Hiring matrix breakdown
              </div>
              {candidate.matrix_breakdown.map((b) => (
                <div key={b.criterion} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-near-black">{b.criterion}</span>
                    <span className="tabular-nums">
                      <span className={`text-base font-bold ${scoreColor(b.score)}`}>{Math.round(b.score)}</span>
                      <span className="text-xs text-text-secondary"> / 100</span>
                      <span className="ml-1 text-xs text-text-secondary">(w {b.weight})</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-secondary">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, b.score))}%`, backgroundColor: barColor(b.score) }}
                    />
                  </div>
                  {b.notes && (
                    <p className="text-xs leading-[1.43] text-text-secondary">{b.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {candidate.filter_results && candidate.filter_results.length > 0 && (
            <div className="mt-4 space-y-2 border-t border-surface-secondary pt-4">
              <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
                Filter requirements
              </div>
              <div className="space-y-1.5">
                {candidate.filter_results.map((f) => {
                  const styles =
                    f.status === "pass" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : f.status === "fail" ? "bg-rose-50 border-rose-200 text-error-text"
                    : "bg-amber-50 border-amber-200 text-amber-800";
                  const icon = f.status === "pass" ? "✓" : f.status === "fail" ? "✕" : "?";
                  return (
                    <div key={f.filter_id} className={`flex items-start gap-2 rounded-btn border ${styles} px-3 py-1.5 text-xs`}>
                      <span className="font-bold">{icon}</span>
                      <div className="flex-1">
                        <span className="font-semibold">{f.label}:</span> {f.reason}
                      </div>
                    </div>
                  );
                })}
              </div>
              {indeterminateFilters.length > 0 && !candidate.auto_rejected && (
                <p className="text-[10px] italic text-text-secondary">
                  Some filters could not be verified from the resume — needs manual review.
                </p>
              )}
            </div>
          )}

          {/* Action buttons row */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-surface-secondary pt-4">
            {onViewProfile && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewProfile(candidate.id); }}
                className="flex items-center gap-2 rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
                View Full Profile
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={handleResumeUpload}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              disabled={reUploading || reEvaluating}
              className="flex items-center gap-2 rounded-btn border border-border-card bg-white px-4 py-2 text-sm font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black disabled:opacity-50"
              title="Replace resume and auto re-evaluate"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m-6 0V5.25A2.25 2.25 0 0 1 11.25 3h1.5A2.25 2.25 0 0 1 15 5.25V8.25m-6 0h6m-3 6.75v-6m0 0L9 11.25m3-3 3 3" />
              </svg>
              {reUploading ? "Uploading…" : "Re-upload resume"}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAddInfo(!showAddInfo); }}
              disabled={reEvaluating}
              className="flex items-center gap-2 rounded-btn border border-border-card bg-white px-4 py-2 text-sm font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black disabled:opacity-50"
              title="Add additional applicant information and auto re-evaluate"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
              Add info & re-evaluate
            </button>
          </div>

          {showAddInfo && (
            <div className="mt-3 rounded-badge border border-border-card bg-surface-secondary p-3" onClick={(e) => e.stopPropagation()}>
              <label className="text-xs font-semibold text-text-secondary">
                Additional information (certifications, training, recent achievements, references, etc.)
              </label>
              <textarea
                value={addInfoText}
                onChange={(e) => setAddInfoText(e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
                placeholder="e.g. Recently completed AWS Solutions Architect certification, attended Google I/O 2024…"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  onClick={() => { setShowAddInfo(false); setAddInfoText(""); }}
                  className="rounded-btn px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-near-black"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddInfo}
                  disabled={!addInfoText.trim()}
                  className="rounded-btn bg-near-black px-3 py-1.5 text-xs font-medium text-white hover:bg-rausch disabled:opacity-50"
                >
                  Submit & re-evaluate
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 border-t border-surface-secondary pt-4">
            <StageActions candidateId={candidate.id} currentStage={candidate.current_stage} />
          </div>

          {isSuperAdmin(currentUser) && (
            <div className="mt-3 border-t border-surface-secondary pt-3">
              <OrgAssignRow candidateId={candidate.id} currentOrgId={candidate.org_id ?? null} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
